import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useDrawingRoom } from '../contexts/DrawingRoomContext';
import { OptimizedExcalidraw } from './OptimizedExcalidraw';

interface BoardViewProps {
  roomId: string;
  isActive?: boolean;
}

// This component ensures Excalidraw is only loaded client-side
const BoardView = ({ roomId, isActive = true }: BoardViewProps) => {
  console.log('🔵 BoardView render - isActive:', isActive, 'roomId:', roomId);
  
  const { permissions } = usePermissions();
  const isReadOnly = !(permissions?.can_write_board);
  const [isMounted, setIsMounted] = useState(false);
  const excalidrawRef = useRef<any>(null);
  
  // Track previous values for debugging
  const prevElementsRef = useRef<any[]>([]);
  const prevAppStateRef = useRef<any>(null);
  const renderCount = useRef(0);
  
  const {
    joinRoom,
    leaveRoom,
    elements = [],
    appState = {},
    version,
    handleLocalChange,
  } = useDrawingRoom();
  
  // Log when elements or appState change
  useEffect(() => {
    console.group('📊 State Update');
    console.log('🔄 Elements changed - count:', elements?.length || 0);
    console.log('📝 AppState changed:', appState);
    console.log('🔍 Previous elements count:', prevElementsRef.current?.length || 0);
    console.groupEnd();
    
    prevElementsRef.current = elements;
    prevAppStateRef.current = appState;
  }, [elements, appState]);
  
  // Memoize the update function
  const updateScene = useCallback(() => {
    if (!excalidrawRef.current) {
      console.log('❌ Excalidraw ref is not available');
      return;
    }
    
    console.group('🔄 Updating Scene');
    console.log('📝 Current elements count:', elements?.length || 0);
    console.log('🎨 Current appState:', appState);
    
    const newScene = {
      elements: elements || [],
      appState: { 
        ...appState, 
        zenModeEnabled: isReadOnly,
        viewBackgroundColor: '#fafafa'
      }
    };
    
    try {
      console.log('🎯 Setting new scene');
      excalidrawRef.current.updateScene(newScene);
      
      // Verify the update was applied
      if (excalidrawRef.current) {
        const sceneElements = excalidrawRef.current.getSceneElements?.();
        console.log('✅ Scene updated - elements count:', sceneElements?.length || 0);
      }
    } catch (error) {
      console.error('❌ Error updating scene:', error);
    }
    
    console.groupEnd();
  }, [elements, appState, isReadOnly]);
  
  // Update scene when elements or appState change
  useEffect(() => {
    if (!isMounted) return;
    
    console.log('🔄 Elements or appState changed, updating scene...');
    updateScene();
  }, [elements, appState, updateScene, isMounted]);

  // Handle visibility changes
  useEffect(() => {
    if (isActive && excalidrawRef.current) {
      console.log('🎯 BoardView became active, refreshing scene...');
      updateScene();
    }
  }, [isActive, updateScene]);

  // Initialize component and join room
  useEffect(() => {
    console.group('🚀 BoardView Mounting');
    console.log('🆔 Room ID:', roomId);
    console.log('🔑 Permissions:', { can_write: permissions?.can_write_board, isReadOnly });
    
    renderCount.current += 1;
    console.log('🔄 Render count:', renderCount.current);
    
    setIsMounted(true);
    
    if (roomId) {
      const tempUserId = `user-${Math.random().toString(36).substr(2, 9)}`;
      const userName = `User-${Math.floor(Math.random() * 1000)}`;
      
      console.log('🔗 Joining room:', { roomId, tempUserId, userName });
      joinRoom(roomId, tempUserId, userName);
      
      // Log initial state after a short delay
      setTimeout(() => {
        console.log('🏁 Initial room state - elements:', elements?.length || 0, 'appState:', appState);
      }, 500);
    }
    
    return () => {
      console.log('👋 BoardView unmounting, leaving room');
      leaveRoom();
      console.groupEnd();
    };
  }, [roomId]);

  // Wait until component is mounted
  if (!isMounted) {
    console.log('⏳ Waiting for client-side mount...');
    return null;
  }

  console.log('🎨 Rendering BoardView with isActive:', isActive);
  
  return (
    <div style={{ 
      position: 'absolute',
      top: '1px',
      left: '1px',
      right: '1px',
      bottom: '1px',
      backgroundColor: '#fafafa',
      display: isActive ? 'flex' : 'none',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <OptimizedExcalidraw
        ref={excalidrawRef}
        key={`${roomId}-${isReadOnly}`}

        initialElements={elements}
        initialAppState={{ 
          ...appState, 
          zenModeEnabled: isReadOnly,
          viewBackgroundColor: '#fafafa',
          gridSize: 20,
          currentItemFontFamily: 1,
          currentItemRoundness: 'round',
          currentItemStrokeColor: '#1e1e1e',
          currentItemBackgroundColor: 'transparent',
          currentItemFillStyle: 'hachure',
          currentItemStrokeWidth: 1,
          currentItemOpacity: 100,
          currentItemTextAlign: 'left',
          currentItemStrokeStyle: 'solid',
          currentItemRoughness: 1,
          currentItemLinearStrokeSharpness: 'round',
          currentItemStartArrowhead: null,
          currentItemEndArrowhead: null,
          currentItemRounded: false,
          currentItemLineHeight: 1.25,
          currentItemFontSize: 16,
          currentItemFont: 1,
          currentItemText: '',
          currentItemStrokeSharpness: 'sharp'
        }}
        onChange={(els, app) => {
          if (!isActive) {
            return;
          }
          handleLocalChange(els, app);
        }}
        isReadOnly={isReadOnly}
        viewModeEnabled={isReadOnly}
        zenModeEnabled={isReadOnly}
        gridModeEnabled={false}
      />
    </div>
  );
};

// Set display name for better debugging
BoardView.displayName = 'BoardView';

// Export as both named and default for backward compatibility
export { BoardView };
export default BoardView;
